import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "../../lib/api";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../ui/dialog";
import { StatusBadge } from "../badges";
import { Progress } from "../ui/progress";
import { toast } from "sonner";
import { Plus, Trash2, Package, IndianRupee, PackageCheck, FileSpreadsheet, AlertTriangle } from "lucide-react";

const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const TODAY = new Date().toISOString().slice(0, 10);
const isOverdue = (i) => i.status !== "received" && i.expected_date && i.expected_date < TODAY;
const EMPTY = { part_number: "", name: "", description: "", category: "General", quantity: 1, unit_cost: 0, supplier: "", status: "needed", expected_date: "", task_id: null };

export default function BomTab({ project, tasks }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("all");
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => api.get(`/projects/${project.id}/bom`).then((r) => setItems(r.data)), [project.id]);
  useEffect(() => { load(); }, [load]);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/projects/${project.id}/bom`, {
        ...form,
        quantity: Number(form.quantity) || 1,
        unit_cost: Number(form.unit_cost) || 0,
        expected_date: form.expected_date || null,
      });
      toast.success("Part added to BOM");
      setOpen(false); setForm(EMPTY);
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const patch = async (id, updates) => {
    try { await api.patch(`/bom/${id}`, updates); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Remove ${item.part_number} — ${item.name} from the BOM?`)) return;
    try { await api.delete(`/bom/${item.id}`); toast.success("Part removed"); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const exportExcel = async () => {
    try {
      const r = await api.get(`/projects/${project.id}/bom/export`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `BOM_${project.key}_${new Date().toISOString().slice(0, 10).replace(/-/g, "")}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("BOM exported to Excel");
    } catch (err) { toast.error(apiError(err)); }
  };

  const taskById = (id) => tasks.find((t) => t.id === id);
  const total = items.reduce((s, i) => s + i.quantity * i.unit_cost, 0);
  const receivedCost = items.filter((i) => i.status === "received").reduce((s, i) => s + i.quantity * i.unit_cost, 0);
  const received = items.filter((i) => i.status === "received").length;
  const overdueItems = items.filter(isOverdue);
  const shown = filter === "all" ? items : filter === "overdue" ? items.filter(isOverdue) : items.filter((i) => i.status === filter);
  const categories = [...new Set(shown.map((i) => i.category))];

  return (
    <div className="space-y-6" data-testid="bom-tab-content">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 stagger">
        <Card className="p-4">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1"><Package className="h-3 w-3" /> Parts</p>
          <p className="font-heading text-2xl font-bold mt-1" data-testid="bom-total-parts">{items.length}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1"><IndianRupee className="h-3 w-3" /> Total Cost</p>
          <p className="font-heading text-2xl font-bold mt-1" data-testid="bom-total-cost">{money(total)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1"><IndianRupee className="h-3 w-3" /> Received Value</p>
          <p className="font-heading text-2xl font-bold mt-1">{money(receivedCost)}</p>
        </Card>
        <Card className="p-4">
          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1"><PackageCheck className="h-3 w-3" /> Received</p>
          <p className="font-heading text-2xl font-bold mt-1">{received}/{items.length}</p>
          <Progress value={items.length ? (received / items.length) * 100 : 0} className="h-1.5 mt-2" />
        </Card>
      </div>

      {overdueItems.length > 0 && (
        <div data-testid="bom-overdue-alert"
          className="border border-rose-500/40 bg-rose-500/10 rounded-md px-4 py-3 flex items-center gap-2.5">
          <AlertTriangle className="h-4 w-4 text-rose-500 shrink-0" />
          <p className="text-sm text-rose-600 dark:text-rose-400">
            <span className="font-semibold">{overdueItems.length} part{overdueItems.length > 1 ? "s" : ""} overdue</span> — past expected delivery:{" "}
            {overdueItems.slice(0, 4).map((i) => i.part_number).join(", ")}{overdueItems.length > 4 ? "…" : ""}
          </p>
          <Button variant="outline" size="sm" className="ml-auto shrink-0 border-rose-500/40 text-rose-600 dark:text-rose-400 hover:text-rose-600"
            data-testid="bom-show-overdue-btn" onClick={() => setFilter("overdue")}>
            Show overdue
          </Button>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-40" data-testid="bom-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {["needed", "ordered", "received"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            <SelectItem value="overdue">⚠ overdue</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2">
          <Button variant="outline" data-testid="bom-export-btn" onClick={exportExcel} disabled={!items.length}>
            <FileSpreadsheet className="h-4 w-4 mr-1" /> Export Excel
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-bom-item-btn"><Plus className="h-4 w-4 mr-1" /> Add Part</Button>
            </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-heading">Add BOM Part</DialogTitle></DialogHeader>
            <form onSubmit={create} className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Part number</Label>
                  <Input data-testid="bom-part-number-input" placeholder="RES-0402-10K" value={form.part_number} onChange={(e) => setForm({ ...form, part_number: e.target.value })} required /></div>
                <div className="col-span-2 space-y-1.5"><Label>Name</Label>
                  <Input data-testid="bom-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
              </div>
              <div className="space-y-1.5"><Label>Description</Label>
                <Textarea data-testid="bom-description-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1.5"><Label>Category</Label>
                  <Input data-testid="bom-category-input" placeholder="Electronics" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Quantity</Label>
                  <Input type="number" min="0" step="any" data-testid="bom-quantity-input" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} required /></div>
                <div className="space-y-1.5"><Label>Unit cost (₹)</Label>
                  <Input type="number" min="0" step="0.01" data-testid="bom-unit-cost-input" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Supplier</Label>
                  <Input data-testid="bom-supplier-input" placeholder="Robu.in" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Expected delivery</Label>
                  <Input type="date" data-testid="bom-expected-date-input" value={form.expected_date} onChange={(e) => setForm({ ...form, expected_date: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Status</Label>
                  <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                    <SelectTrigger data-testid="bom-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["needed", "ordered", "received"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select></div>
                <div className="space-y-1.5"><Label>Needed for task</Label>
                  <Select value={form.task_id || "none"} onValueChange={(v) => setForm({ ...form, task_id: v === "none" ? null : v })}>
                    <SelectTrigger data-testid="bom-task-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      {tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.tid} — {t.title.slice(0, 30)}</SelectItem>)}
                    </SelectContent>
                  </Select></div>
              </div>
              <Button type="submit" data-testid="bom-create-submit-btn" className="w-full">Add Part</Button>
            </form>
          </DialogContent>
          </Dialog>
        </div>
      </div>

      {categories.map((cat) => (
        <div key={cat} className="space-y-2">
          <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground">{cat} ({shown.filter((i) => i.category === cat).length})</p>
          <div className="border border-border rounded-md overflow-x-auto">
            <table className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-border bg-card text-left">
                  {["Part #", "Name", "Qty", "Unit Cost", "Total", "Supplier", "Expected", "Task", "Status", ""].map((h, i) => (
                    <th key={i} className="px-3 py-2 text-[10px] font-mono uppercase tracking-wider text-muted-foreground font-medium">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {shown.filter((i) => i.category === cat).map((item) => (
                  <tr key={item.id} className={`transition-colors ${isOverdue(item) ? "bg-rose-500/10 hover:bg-rose-500/15" : "hover:bg-accent/40"}`} data-testid="bom-item-row">
                    <td className="px-3 py-2.5 font-mono text-xs text-primary whitespace-nowrap">{item.part_number}</td>
                    <td className="px-3 py-2.5 max-w-[220px]">
                      <p className="font-medium truncate">{item.name}</p>
                      {item.description && <p className="text-xs text-muted-foreground truncate">{item.description}</p>}
                    </td>
                    <td className="px-3 py-2.5 font-mono text-xs">{item.quantity}</td>
                    <td className="px-3 py-2.5 font-mono text-xs">{money(item.unit_cost)}</td>
                    <td className="px-3 py-2.5 font-mono text-xs font-semibold">{money(item.quantity * item.unit_cost)}</td>
                    <td className="px-3 py-2.5 text-xs text-muted-foreground">{item.supplier || "—"}</td>
                    <td className="px-3 py-2.5 font-mono text-[10px] whitespace-nowrap">
                      {isOverdue(item) ? (
                        <span className="text-rose-500 font-semibold flex items-center gap-1" data-testid="bom-overdue-date">
                          <AlertTriangle className="h-3 w-3" /> {item.expected_date}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">{item.expected_date || "—"}</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {item.task_id ? <span className="text-[10px] font-mono border border-border rounded px-1.5 py-0.5 text-muted-foreground">{taskById(item.task_id)?.tid || "?"}</span> : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="px-3 py-2.5">
                      <Select value={item.status} onValueChange={(v) => patch(item.id, { status: v })}>
                        <SelectTrigger className="w-28 h-7 text-xs border-0 bg-transparent p-0 justify-start gap-1" data-testid="bom-item-status-select">
                          <StatusBadge status={item.status} />
                        </SelectTrigger>
                        <SelectContent>{["needed", "ordered", "received"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-3 py-2.5">
                      <Button variant="ghost" size="icon" className="h-7 w-7" data-testid="bom-item-delete-btn" onClick={() => remove(item)}>
                        <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      {!shown.length && <p className="text-sm text-muted-foreground">No parts {filter !== "all" ? `with status "${filter}"` : "in the BOM yet. Add the first part above."}</p>}
    </div>
  );
}
